import React, { useCallback, useEffect, useState } from 'react';
import { authStore } from '../../api/client';
import {
  candidateTalentOptIn,
  candidateTalentOptOut,
  getCandidateProfile,
  uploadCandidateResume,
} from '../../api/candidatePortal';
import Switch from '../../components/ui/Switch';

export default function CandidateProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [poolBusy, setPoolBusy] = useState(false);
  const [message, setMessage] = useState('');
  const user = authStore.user;

  const load = useCallback(async () => {
    try {
      const res = await getCandidateProfile();
      setProfile(res.data);
    } catch (err) {
      console.warn('[Profile]', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMessage('');
    try {
      const res = await uploadCandidateResume(file);
      setProfile(res.data);
      setMessage('Resume uploaded successfully.');
    } catch (err) {
      setMessage(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleTogglePool = async (nextChecked) => {
    setPoolBusy(true);
    setMessage('');
    try {
      if (!nextChecked) {
        await candidateTalentOptOut();
        setMessage('Removed from talent pool.');
      } else {
        await candidateTalentOptIn();
        setMessage('You are now visible in the talent pool.');
      }
      await load();
    } catch (err) {
      // Never report a visibility change that did not happen (privacy): keep the
      // server state and tell the candidate it failed.
      setMessage(err.data?.message
        || (nextChecked
          ? 'Could not add you to the talent pool. Please try again.'
          : 'Could not hide your profile. Please try again.'));
      await load();
    } finally {
      setPoolBusy(false);
    }
  };

  // Real profile values only: no sample skills, rating, count or visibility.
  const skillsList = profile?.skills?.length ? profile.skills : [];
  const ratingScore = profile?.samastixRating != null ? Number(profile.samastixRating) : null;
  const interviewCount = profile?.interviewCount ?? 0;
  const isVisible = profile?.talentPoolVisible === true;

  if (loading) {
    return (
      <div className="py-24 text-center">
        <div className="w-8 h-8 border-2 border-[var(--teal-500)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-[var(--ink-soft)]">Loading profile…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div>
        <h1
          className="text-2xl sm:text-[26px] font-semibold text-[var(--navy-950)] tracking-tight"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          My Profile
        </h1>
        <p className="text-[13.5px] text-[var(--ink-soft)] mt-1">
          Resume, skills, and talent pool visibility.
        </p>
      </div>

      {message && (
        <div className="p-3.5 rounded-[10px] bg-[var(--teal-050)] border border-[var(--teal-100)] text-xs font-medium text-[var(--teal-700)] flex items-center justify-between">
          <span>{message}</span>
          <button
            type="button"
            onClick={() => setMessage('')}
            className="text-[var(--teal-600)] hover:text-[var(--teal-700)] font-bold ml-3"
          >
            ×
          </button>
        </div>
      )}

      {/* ── 2-Column Grid Layout ─────────────────────────────────────── */}
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Left Column: Resume + Talent Pool */}
        <div className="lg:col-span-7 space-y-6">
          {/* Resume Card */}
          <div className="card p-6">
            <h3
              className="text-sm font-semibold text-[var(--navy-950)]"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Resume
            </h3>
            <p className="text-xs text-[var(--ink-soft)] mt-0.5">
              PDF or plain text. Used for job matching and recruiter search.
            </p>

            {/* Document Box */}
            <div className="mt-4 rounded-[12px] border border-[var(--line)] bg-[var(--canvas)] p-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-[8px] bg-[var(--rose-100)] text-[var(--rose-600)] flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[var(--navy-950)] truncate">
                    {profile?.resumeFileName || `${user?.fullName?.replace(/\s+/g, '_') || 'Candidate'}_Resume.pdf`}
                  </p>
                  <p className="text-[11px] text-[var(--ink-faint)] mt-0.5">
                    Uploaded Sep 1, 2026 · 214 KB
                  </p>
                </div>
              </div>

              <label className="btn btn-ghost !py-1.5 !px-3.5 !text-xs cursor-pointer shrink-0">
                {uploading ? 'Uploading…' : 'Replace'}
                <input
                  type="file"
                  accept=".pdf,text/plain"
                  className="hidden"
                  onChange={onFile}
                  disabled={uploading}
                />
              </label>
            </div>

            {/* Skills extracted */}
            <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-[var(--line-soft)]">
              {!skillsList.length && (
                <span className="text-xs text-[var(--ink-faint)]">
                  Skills appear here after you upload a resume.
                </span>
              )}
              {skillsList.map((skill) => (
                <span
                  key={skill}
                  className="chip chip-navy !text-xs !py-1 !px-3 font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* Talent Pool Card */}
          <div className="card p-6">
            <h3
              className="text-sm font-semibold text-[var(--navy-950)]"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Talent pool
            </h3>
            <p className="text-xs text-[var(--ink-soft)] mt-0.5">
              Opt in to let recruiters discover your profile. Requires a resume on file.
            </p>

            <div className="mt-5 pt-4 border-t border-[var(--line-soft)] flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-[var(--ink)]">
                  Visible to recruiters
                </p>
                <p className="text-[11.5px] text-[var(--ink-faint)] mt-0.5">
                  {isVisible ? 'Currently visible to recruiters' : 'Hidden from recruiter search'}
                </p>
              </div>

              <Switch
                checked={isVisible}
                onChange={handleTogglePool}
                disabled={poolBusy}
                aria-label="Toggle talent pool visibility"
              />
            </div>
          </div>
        </div>

        {/* Right Column: Interview Rating + Account */}
        <div className="lg:col-span-5 space-y-6">
          {/* Interview Rating Card */}
          <div className="card p-6">
            <h3
              className="text-sm font-semibold text-[var(--navy-950)]"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Interview rating
            </h3>
            <p className="text-xs text-[var(--ink-soft)] mt-0.5">
              A rolling average across your completed interviews.
            </p>

            <div className="mt-4 rounded-[12px] bg-[var(--teal-050)] border border-[var(--teal-100)] p-4 flex items-baseline gap-2.5">
              <span
                className="text-2xl font-bold text-[var(--navy-950)] font-tabular"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {ratingScore != null ? Math.round(ratingScore) : '-'}
                {ratingScore != null && <span className="text-sm font-normal text-[var(--ink-faint)]">/100</span>}
              </span>
              <span className="text-xs text-[var(--ink-soft)]">
                {ratingScore != null
                  ? `Based on ${interviewCount} completed interview${interviewCount === 1 ? '' : 's'}`
                  : 'No rated interviews yet'}
              </span>
            </div>
          </div>

          {/* Account Card */}
          <div className="card p-6">
            <h3
              className="text-sm font-semibold text-[var(--navy-950)]"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Account
            </h3>
            <p className="text-xs text-[var(--ink-soft)] mt-2">
              {user?.email || 'devika.krishnan@example.com'}
            </p>

            <button
              type="button"
              onClick={() => alert('Contact details editor opened.')}
              className="btn btn-ghost w-full justify-center mt-4 !py-2 !text-xs"
            >
              Edit contact details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
